import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LandlordContact,
  Me,
  MeUpdate,
  Property,
  PropertyCreate,
  PropertySettings,
  PropertyUpdate,
} from '@vuokramani/shared';
import { api } from './api.ts';
import { meQueryKey } from './auth.ts';

export function useProperties(role?: 'owner' | 'tenant') {
  return useQuery({
    queryKey: ['properties', role ?? 'all'],
    queryFn: () =>
      api<{ properties: Property[] }>(`/properties${role ? `?role=${role}` : ''}`).then(
        (r) => r.properties,
      ),
  });
}

export function useArchivedProperties() {
  return useQuery({
    queryKey: ['properties', 'archived'],
    queryFn: () =>
      api<{ properties: Property[] }>('/properties?archived=true&role=owner').then(
        (r) => r.properties,
      ),
  });
}

export function useProperty(id: string | undefined) {
  return useQuery({
    queryKey: ['property', id],
    queryFn: () => api<Property>(`/properties/${id}`),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useCreateProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PropertyCreate) => api<Property>('/properties', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useUpdateProperty(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PropertyUpdate) =>
      api<Property>(`/properties/${id}`, { method: 'PATCH', body }),
    onSuccess: (property) => {
      queryClient.setQueryData(['property', id], property);
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

/** Asuntokohtaiset asetukset. Muutos kirjautuu lokiin. */
export function useUpdatePropertySettings(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PropertySettings) =>
      api<Property>(`/properties/${id}/settings`, { method: 'PUT', body }),
    onSuccess: (property) => {
      queryClient.setQueryData(['property', id], property);
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
      void queryClient.invalidateQueries({ queryKey: ['events', id] });
    },
  });
}

export function useArchiveProperty(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: 'archive' | 'restore') =>
      api<void>(`/properties/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['property', id] });
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

/** Vuokranantajan yhteystiedot sellaisina kuin vuokralainen ne näkee. */
export function useLandlords(id: string | undefined) {
  return useQuery({
    queryKey: ['property', id, 'landlords'],
    queryFn: () =>
      api<{ landlords: LandlordContact[] }>(`/properties/${id}/landlords`).then((r) => r.landlords),
    enabled: Boolean(id),
  });
}

export function useUpdateMe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MeUpdate) => api<Me>('/auth/me', { method: 'PATCH', body }),
    onSuccess: (me) => {
      queryClient.setQueryData(meQueryKey, me);
      void queryClient.invalidateQueries({ queryKey: ['property'] });
    },
  });
}

/** Listoissa näytettävä nimi: käyttäjän oma nimi tai osoite. */
export function propertyTitle(p: Pick<Property, 'name' | 'streetAddress'>): string {
  return p.name || p.streetAddress;
}

export function formatPercentFromBp(bp: number, locale = 'fi-FI'): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(bp / 100);
}

/** Lomakkeen tekstikenttä luvuksi: "45,5" → 45.5, tyhjä → null. */
export function toNumber(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  return trimmed === '' ? null : Number(trimmed);
}
